import { Controller } from "@hotwired/stimulus"

// Autocomplete 컨트롤러 - Design System v3
// Fomantic UI Dropdown 기반의 비동기 검색 자동완성
export default class extends Controller {
  static targets = ["dropdown", "input", "menu"]

  static values = {
    url: { type: String, default: "" },
    placeholder: { type: String, default: "검색어를 입력하세요" },
    minInputLength: { type: Number, default: 1 },
    valueField: { type: String, default: "value" },
    labelField: { type: String, default: "label" },
    clearable: { type: Boolean, default: true },
    // true 이면 라벨의 첫 칸(' | ' 기준, 보통 제품명)을 개나리색 배경으로 강조
    highlightFirst: { type: Boolean, default: false },
    // true 이면 선택 후에도 입력창에서 검색어를 직접 편집 가능(포커스 시 현재 라벨 prefill)
    editable: { type: Boolean, default: false },
    // 매 요청에 함께 보낼 추가 쿼리 파라미터 (예: { sort: 'date' }).
    // 외부에서 data-autocomplete-extra-params-value 를 갱신하면
    // extraParamsValueChanged 가 호출되어 현재 검색어로 재검색.
    extraParams: { type: Object, default: {} }
  }

  connect() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      this.initializeDropdown()
    } else {
      this.waitForJQuery().then(() => {
        this.initializeDropdown()
      })
    }
  }

  disconnect() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      try {
        $(this.dropdownTarget).dropdown('destroy')
      } catch (e) {
        // ignore
      }
    }
  }

  waitForJQuery() {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 50)

      setTimeout(() => {
        clearInterval(checkInterval)
        resolve()
      }, 5000)
    })
  }

  initializeDropdown() {
    if (typeof $ === 'undefined' || !$.fn || !$.fn.dropdown) {
      return
    }

    const self = this
    const $dropdown = $(this.dropdownTarget)

    const options = {
      placeholder: this.placeholderValue,
      clearable: this.clearableValue,
      minCharacters: this.minInputLengthValue,
      duration: 0,
      transition: 'none',
      message: {
        noResults: '검색 결과가 없습니다'
      },
      apiSettings: {
        url: this.urlValue + '?q={query}',
        // 같은 검색어라도 항상 서버에서 재조회 (Fomantic 기본 cache:'local' 비활성)
        cache: false,
        beforeSend: function(settings) {
          // 최소 입력 길이 체크
          const query = settings.urlData.query || ''
          if (query.length < self.minInputLengthValue) {
            return false
          }
          // extraParams 동적 첨부 (jQuery $.ajax data 표준).
          const extra = self.extraParamsValue || {}
          settings.data = settings.data || {}
          Object.entries(extra).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== '') settings.data[k] = v
          })
          return settings
        },
        onResponse: function(response) {
          // API 응답을 Fomantic UI 형식으로 변환
          return self.transformResponse(response)
        },
        onError: function() {
          return { success: false, results: [] }
        }
      },
      saveRemoteData: false,
      filterRemoteData: false
    }

    // 선택 후에도 검색어를 직접 편집 가능하게 — dropdown_controller 의 검색형 설정 차용
    if (this.editableValue) {
      options.forceSelection = false
      options.selectOnKeydown = false
      options.showOnFocus = true
    }

    $dropdown.dropdown(options)

    // 메뉴 최대 표시 항목 수 확장 (기본 ~6개 → 10개)
    $dropdown.find('.menu').css('max-height', '360px')

    // editable: 입력창 포커스 시 현재 선택된 라벨을 검색칸에 채워 일부 수정/재검색 가능하게
    //
    // ⚠️ 여기서 dropdown('search') 를 부르지 않는다 — **자기가 자기를 깨우는 고리**가 된다.
    //
    //   click → focusSearch → focus → dropdown('search') → 서버 조회 ①
    //                                        ↓
    //                          ①의 onSuccess 가 focusSearch 를 다시 부른다
    //                                        ↓
    //                            focus → dropdown('search') → 서버 조회 ②
    //
    // 두 번에서 멈추는 건 아래 `$search.val()` 가드 덕이다. 그게 없었으면 계속 돌았다.
    // 브라우저 콘솔의 호출 스택으로 확인했다(2026-08-25) — 두 조회 모두 이 줄이 쐈고,
    // Fomantic 이 스스로 조회한 것이 아니었다. search_by_product_name 은 DB 를 125ms
    // 쓰므로 선택·열기마다 250ms 를 버리고 있었다.
    //
    // 지워서 잃는 것은 "다시 열었을 때 목록을 새로 받는 것" 뿐이다. Fomantic 이 직전에
    // 그린 항목을 DOM 에 두므로 대개 아까 보던 목록이 그대로 보이고, 한 글자라도 고치면
    // native input 핸들러가 서버로 재검색해 새 후보를 채운다.
    //
    // 이 핸들러의 본래 목적(선택된 라벨을 검색칸에 채워 수정 가능하게)은 아래
    // `$search.val(label)` 이 그대로 달성한다.
    if (this.editableValue) {
      const $search = $dropdown.find('input.search')
      $search.on('focus', function() {
        if ($search.val()) return                 // 이미 편집 중이면 무시
        const $text = $dropdown.find('> .text')
        if ($text.hasClass('default')) return     // 선택 없음(placeholder 상태)
        const label = $text.text().trim()
        if (!label) return
        $search.val(label)
        // 커서를 끝에 두어 글자 단위 수정 가능. 한 글자 수정마다 Fomantic native input
        // 핸들러가 서버(apiSettings)로 재검색 → 새 후보를 메뉴에 다시 채움
        const len = label.length
        $search[0].setSelectionRange(len, len)
      })
    }
  }

  transformResponse(response) {
    // 다양한 응답 형식 지원
    let items = []

    if (Array.isArray(response)) {
      // 단순 배열: [{ value: '1', label: 'Option 1' }, ...]
      items = response
    } else if (response.data && Array.isArray(response.data)) {
      // { data: [...] } 형식
      items = response.data
    } else if (response.results && Array.isArray(response.results)) {
      // Fomantic UI 형식: { success: true, results: [...] }
      return response
    } else if (response.success && response.results) {
      return response
    }

    // Fomantic UI가 기대하는 형식으로 변환
    // Fomantic UI는 'name'과 'value' 필드를 사용
    // Fomantic 은 `{name, value}` 만 받는다(apiSettings 계약) — 그래서 아래 map 이 원본의
    // 나머지 필드를 버린다. **원본을 value 로 색인해 남겨 둔다.**
    //
    // 없으면 선택 시점에 그 값들을 다시 서버에서 받아 와야 한다. 실제로 MSDS 검색이
    // 그랬다 — 응답에 이미 있던 product_name·manufacturer 를 여기서 버리고,
    // 후보를 클릭할 때 `GET /workplace/msds/ref/<ref>` 로 같은 값을 또 받았다.
    // 선택 핸들러는 `itemFor(value)` 로 읽는다.
    this.itemsByValue = {}
    items.forEach(item => {
      const value = item[this.valueFieldValue] || item.value
      if (value !== undefined && value !== null) this.itemsByValue[String(value)] = item
    })
    // 화면 inline JS 는 Stimulus 인스턴스를 직접 못 잡는다 — 이미 wrapper 를
    // `closest('[data-controller="autocomplete"]')` 로 찾고 있으므로 거기에 얹어 둔다.
    this.element.autocompleteItems = this.itemsByValue

    const results = items.map(item => ({
      name: this.buildName(item[this.labelFieldValue] || item.name || item.label),
      value: item[this.valueFieldValue] || item.value
    }))

    return {
      success: true,
      results: results
    }
  }

  // 마지막 검색 응답에서 그 value 의 **원본 항목**을 돌려준다(없으면 null).
  // 메뉴가 새 응답으로 갈리면 이 표도 함께 갈린다 — 옛 항목을 클릭하는 경우
  // null 이 나오므로, 호출부는 그때를 실패로 다뤄야 한다.
  itemFor(value) {
    return (this.itemsByValue || {})[String(value)] || null
  }

  // 라벨 문자열을 메뉴 표시용으로 가공. highlightFirst 면 첫 칸을 개나리색 배경으로 강조.
  buildName(label) {
    if (!this.highlightFirstValue || typeof label !== 'string') {
      return label
    }
    const parts = label.split(' | ')
    // 제품명(첫 칸) — navy 배경 + 흰 글자 (제조사명과 색상 맞바꿈)
    parts[0] = '<span style="background-color:#000080;color:#FFFFFF;padding:0 2px;border-radius:2px;">' + this.escapeHtml(parts[0]) + '</span>'
    // 제조사명(둘째 칸) — 녹색 배경 + 흰 글자
    if (parts.length > 1) {
      parts[1] = '<span style="background-color:#2E7D32;color:#FFFFFF;padding:0 2px;border-radius:2px;">' + this.escapeHtml(parts[1]) + '</span>'
    }
    return parts.join(' | ')
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  // 프로그래밍 방식으로 값 설정
  setValue(value) {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      $(this.dropdownTarget).dropdown('set selected', value)
    }
  }

  // 드롭다운 초기화
  clear() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      $(this.dropdownTarget).dropdown('clear')
    }
  }

  // 드롭다운 리프레시
  refresh() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      $(this.dropdownTarget).dropdown('refresh')
    }
  }

  // extraParams 값이 외부에서 변경되면 현재 검색어로 재검색을 트리거.
  // 초기 connect 시에도 호출되지만 그땐 입력값이 없어 자연 노옵.
  extraParamsValueChanged() {
    if (typeof $ === 'undefined' || !$.fn || !$.fn.dropdown) return
    if (!this.hasDropdownTarget) return
    const $dropdown = $(this.dropdownTarget)
    const $search = $dropdown.find('input.search')
    const query = $search.val()
    if (query && query.length >= this.minInputLengthValue) {
      $dropdown.dropdown('search')
    }
  }
}
